import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import taskService from '../../services/taskService';
import './TaskBoard.css';

const TaskBoard = () => {
  const navigate = useNavigate();
  const [tasksByStatus, setTasksByStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [projectFilter, setProjectFilter] = useState('');

  const statusColumns = [
    { key: 'To Do', label: 'To Do', color: '#6c757d' },
    { key: 'In Progress', label: 'In Progress', color: '#007bff' },
    { key: 'In Review', label: 'In Review', color: '#ffc107' },
    { key: 'Done', label: 'Done', color: '#28a745' },
    { key: 'Blocked', label: 'Blocked', color: '#dc3545' },
    { key: 'On Hold', label: 'On Hold', color: '#6f42c1' }
  ];

  useEffect(() => {
    fetchTasks();
  }, [projectFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (projectFilter) params.project_id = projectFilter;
      
      const data = await taskService.getTasksByStatus(params);
      setTasksByStatus(data.tasksByStatus || {});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tasks');
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTask(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    
    if (!draggedTask || draggedTask.status === newStatus) {
      return;
    }

    try {
      // Optimistically update UI
      const updatedTasksByStatus = { ...tasksByStatus };
      
      // Remove from old status
      updatedTasksByStatus[draggedTask.status] = updatedTasksByStatus[draggedTask.status].filter(
        t => t.id !== draggedTask.id
      );
      
      // Add to new status
      if (!updatedTasksByStatus[newStatus]) {
        updatedTasksByStatus[newStatus] = [];
      }
      updatedTasksByStatus[newStatus].push({ ...draggedTask, status: newStatus });
      
      setTasksByStatus(updatedTasksByStatus);

      // Update on server
      await taskService.updateTaskStatus(draggedTask.id, newStatus);
      
      // Refresh to get accurate data
      fetchTasks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update task status');
      console.error('Error updating task:', err);
      // Revert on error
      fetchTasks();
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Low': '#28a745',
      'Medium': '#ffc107',
      'High': '#fd7e14',
      'Critical': '#dc3545'
    };
    return colors[priority] || '#6c757d';
  };

  const handleTaskClick = (taskId) => {
    navigate(`/app/tasks/${taskId}`);
  };

  if (loading) {
    return (
      <div className="task-board-container">
        <div className="loading">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="task-board-container">
      <div className="task-board-header">
        <h1>Task Board</h1>
        <div className="board-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/tasks/new')}
          >
            + New Task
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/tasks')}
          >
            List View
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="kanban-board">
        {statusColumns.map(column => (
          <div 
            key={column.key}
            className="kanban-column"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.key)}
          >
            <div 
              className="column-header"
              style={{ borderTopColor: column.color }}
            >
              <h3>{column.label}</h3>
              <span className="task-count">
                {tasksByStatus[column.key]?.length || 0}
              </span>
            </div>
            
            <div className="column-content">
              {tasksByStatus[column.key]?.map(task => (
                <div
                  key={task.id}
                  className="task-card"
                  draggable
                  onDragStart={(e) => handleDragStart(e, task)}
                  onDragEnd={handleDragEnd}
                  onClick={() => handleTaskClick(task.id)}
                >
                  <div className="task-card-header">
                    <span 
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(task.priority) }}
                    >
                      {task.priority}
                    </span>
                    {task.project && (
                      <span className="project-code">{task.project.code}</span>
                    )}
                  </div>
                  
                  <h4 className="task-title">{task.title}</h4>
                  
                  {task.description && (
                    <p className="task-description">
                      {task.description.length > 100 
                        ? `${task.description.substring(0, 100)}...` 
                        : task.description}
                    </p>
                  )}
                  
                  <div className="task-card-footer">
                    {task.assignee && (
                      <div className="assignee-info">
                        {task.assignee.avatar_url ? (
                          <img 
                            src={task.assignee.avatar_url} 
                            alt={`${task.assignee.first_name} ${task.assignee.last_name}`}
                            className="assignee-avatar"
                          />
                        ) : (
                          <div className="assignee-avatar-placeholder">
                            {task.assignee.first_name?.[0]}{task.assignee.last_name?.[0]}
                          </div>
                        )}
                        <span className="assignee-name">
                          {task.assignee.first_name} {task.assignee.last_name}
                        </span>
                      </div>
                    )}
                    
                    {task.due_date && (
                      <div className="due-date">
                        📅 {new Date(task.due_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  
                  {task.tags && task.tags.length > 0 && (
                    <div className="task-tags">
                      {task.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {(!tasksByStatus[column.key] || tasksByStatus[column.key].length === 0) && (
                <div className="empty-column">
                  <p>No tasks</p>
                  <p className="empty-hint">Drag tasks here</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaskBoard;

